<template>
  <div class="container mb-4">
      <DataTable :value="actualOrderRows" :autoLayout="true" :contextMenuSelection.sync="selectedRow" @row-contextmenu="onRowContextMenu" @row-reorder="onRowReorder" :rowClass="rowClass">
        <template #header>
          <div class="p-d-flex p-jc-between">
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon" style="color: black">
                {{invoiceName}}
              </span>
              <span class="p-inputgroup-addon">
                <i class="pi pi-calendar"></i>
              </span>
              <InputText v-model="invoiceDate" placeholder="Fakturadatum" />
              <span class="p-inputgroup-addon">
                <i class="pi pi-id-card"></i>
              </span>
              <InputText v-model="deliveryPeriod" placeholder="Leveransperiod" />
              <span class="p-inputgroup-addon">
                <i class="pi pi-money-bill"></i>
              </span>
              <DropDown v-model="dmt" :options="INVOICE_DMTS" optionLabel="data.dmtString" />
              <DropDown v-model="subContractorInfoText" :options="['TRANSPORT AV EKOFLOCK, VETLANDA -', 'TRANSPORT AV KEMIKALIER, VETLANDA -', 'TRANSPORT AV EKOFLOCK, SÖLVESBORG -', 'TRANSPORT AV KEMIKALIER, SÖLVESBORG -']" placeholder="Välj informationstext för faktura"/>
            </div>
            <div>
              <Button label="Generera faktura" icon="pi pi-refresh" @click="recalculateInvoice()" />
              <template v-if="archive">
                <Button label="Uppdatera faktura" icon="pi pi-plus" :disabled="actualOrderRows.length == 0" @click="updateInvoice()" />
              </template>
              <template v-else>
                <Button label="Spara faktura" icon="pi pi-plus" :disabled="saved || (actualOrderRows.length == 0)" @click="saveInvoice()" />
                <Button label="Skicka faktura" :disabled="!saved || INVOICE_BY_ID(id).state !== 'saved'" icon="pi pi-check" @click="sendInvoice(INVOICE_BY_ID(id).name)" />
              </template>
            </div>
          </div>
        </template>
        <Column :rowReorder="true" headerStyle="width: 3em" :reorderableColumn="false" />
        <Column field="article" header="Artikel" />
        <Column field="text" header="Text" />
        <Column field="amount" header="Antal" bodyStyle="text-align: right" />
        <Column header="Apris" bodyStyle="text-align: right">
          <template #body="slotProps">
            {{ slotProps.data.price === '' ? '' : slotProps.data.price.toFixed(2) | space1k }}
          </template>
        </Column>
        <Column header="Belopp" bodyStyle="text-align: right">
          <template #body="slotProps">
            {{ slotProps.data.amount === '' ? '' : roundUp(slotProps.data.amount * slotProps.data.price, 2).toFixed(2) | space1k }}
          </template>
        </Column>
        <ColumnGroup type="footer">
        <Row>
            <Column footer="Total:" :colspan="5" footerStyle="text-align: right" />
            <Column :footer="getTotal(actualOrderRows) | space1k" footerStyle="text-align: right" />
        </Row>
        </ColumnGroup>
      </DataTable>
      <ContextMenu :model="menuModel" ref="cm" />
      <InvoiceFeralcoCreateSelf v-if="!_.isEmpty(subContractorOrderRows)" :id="isimGenerateId(subContractorId)" :invoice="subContractorOrderRows" :infoText="subContractorInfoText" :printable="saved" />
  </div>
</template>

<script>

import { mapGetters } from 'vuex'

import ProgressSpinner from 'primevue/progressspinner'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import Button from 'primevue/button'
import SplitButton from 'primevue/splitbutton'
import ContextMenu from 'primevue/contextmenu'
import DropDown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import moment from 'moment'

import InvoiceFeralcoCreateSelf from './InvoiceFeralcoCreateSelf'

import { InvoiceDmtMixin } from '@/mixins/InvoiceDmtMixin'
import { RoundingMixin } from '@/mixins/RoundingMixin'
import { InvoiceSubIdMixin } from '@/mixins/InvoiceSubIdMixin'
import { InvoiceSubContractorMixin } from '@/mixins/InvoiceSubContractorMixin'

export default {
  name: 'InvoiceFeralcoCreate',
  mixins: [InvoiceDmtMixin, RoundingMixin, InvoiceSubIdMixin, InvoiceSubContractorMixin],
  components: {
    ProgressSpinner,
    DataTable,
    Column,
    ColumnGroup,
    Row,
    Button,
    SplitButton,
    ContextMenu,
    DropDown,
    InputText,
    InvoiceFeralcoCreateSelf
  },
  data () {
    return {
      actualOrderRows: [],
      deliveryPeriod: '',
      invoiceDate: moment().format('YYYY-MM-DD'),
      invoiceName: '',
      selectedRow: null,
      saved: false,
      menuModel: [
        { label: 'Ny artikel', icon: 'pi pi-fw pi-angle-up', command: () => this.addRowBefore(this.selectedRow) },
        { label: 'Tom rad', icon: 'pi pi-fw pi-angle-up', command: () => this.addEmptyRowBefore(this.selectedRow) },
        { label: 'Ta bort', icon: 'pi pi-fw pi-trash', command: () => this.deleteRow(this.selectedRow) }
      ]
    }
  },
  props: {
    id: {
      type: Number,
      required: true
    },
    archive: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters(['SUB_INVOICE_SERIE_FOR_ID', 'INVOICES', 'INVOICE_BY_ID', 'VISMA_ARTICLES', 'VISMA_ARTICLE_BY_ID', 'FERALCOARTICLE_BY_NAME', 'FERALCOARTICLES', 'INVOICE_DMTS_BY_ID', 'INVOICE_DMTS_BY_STRING', 'TRUCKS_BY_ID'])
  },
  async mounted () {
    await this.$store.dispatch('GET_FERALCOARTICLES')
    await this.$store.dispatch('GET_FINISHED_ORDERS')
    await this.$store.dispatch('GET_INVOICE_ID', { id: this.$props.id })
    await this.$store.dispatch('GET_VISMA_ARTICLES')
    await this.$store.dispatch('GET_TRUCKS')
    const invoice = this.INVOICE_BY_ID(this.$props.id)
    if (invoice !== undefined && invoice.data !== null) {
      const invoiceData = invoice.data
      this.saved = true
      this.invoiceDate = invoiceData.invoiceDate
      this.invoiceName = invoice.name
      this.deliveryPeriod = invoiceData.period ? invoiceData.period : ''
      this.subContractorInfoText = invoiceData.subContractorInfoText
      this.actualOrderRows = invoiceData.orderRows ? invoiceData.orderRows : null
      const backupDmt = this.dmt
      if (invoiceData.dmtId === undefined) {
        this.dmt = this.INVOICE_DMTS_BY_STRING(invoiceData.dmt)
      } else {
        this.dmt = this.INVOICE_DMTS_BY_ID(invoiceData.dmtId)
      }
      this.dmt = this.dmt === undefined ? backupDmt : this.dmt
      this.iscmInitData(invoice)
    }
  },
  methods: {
    getTotal (rows) {
      return rows.reduce((prev, curr) => curr.tag === 'Separator' ? prev : prev + (this.roundUp(curr.amount * curr.price, 2)), 0).toFixed(2)
    },
    addRowBefore (row) {

    },
    addRowAfter (row) {

    },
    addEmptyRowBefore (row) {
      this.actualOrderRows.splice(this.rowIndex(row), 0, this.createVismaSeparatorElement())
    },
    addEmptyRowAfter (row) {
      this.actualOrderRows.splice(this.rowIndex(row) + 1, 0, this.createVismaSeparatorElement())
    },
    deleteRow (row) {
      this.actualOrderRows.splice(this.rowIndex(row), 1)
    },
    rowIndex (row) {
      return this.actualOrderRows.findIndex((el) => el === row)
    },
    onRowContextMenu (event) {
      this.$refs.cm.show(event.originalEvent)
    },
    onRowReorder (event) {
      this.actualOrderRows = event.value
    },
    rowClass (data) {
      return data.tag === 'Error' ? 'error-invoice-row' : null
    },
    recalculateInvoice () {
      this.saved = false
      this.actualOrderRows.splice(0, this.actualOrderRows.length)
      this.actualOrderRows.push(...this.createVismaInvoice(this.createInvoiceRows(this.$props.id)))
      this.iscmCreateInvoice(
        this.actualOrderRows.filter(row => row.subContractor === 191),
        191,
        this.invoiceDate,
        this.getDmtString(),
        this.getDmtId(),
        this.deliveryPeriod,
        '',
        this.subContractorInfoText
      )
    },
    async saveInvoice () {
      this.saved = true
      await this.$store.dispatch('SAVE_INVOICE_DATA', {
        id: this.$props.id,
        data: {
          customer: 1501,
          invoiceDate: this.invoiceDate,
          dmt: this.getDmtString(),
          dmtId: this.getDmtId(),
          period: this.deliveryPeriod,
          subContractorInfoText: this.subContractorInfoText,
          orderRows: this.actualOrderRows
        }
      })
      await this.iscmSaveInvoice(this.INVOICE_BY_ID(this.$props.id), 'feralco')
      await this.$store.dispatch('GET_ACTIVE_INVOICES')
    },
    async updateInvoice () {
      await this.$store.dispatch('UPDATE_INVOICE_DATA', {
        id: this.$props.id,
        data: {
          customer: 1501,
          invoiceDate: this.invoiceDate,
          dmt: this.getDmtString(),
          dmtId: this.getDmtId(),
          period: this.deliveryPeriod,
          subContractorInfoText: this.subContractorInfoText,
          orderRows: this.actualOrderRows
        }
      })
      await this.iscmSaveInvoice(this.INVOICE_BY_ID(this.$props.id), 'feralco')
      await this.$store.dispatch('GET_ACTIVE_INVOICES')
      this.$router.go(-1)
    },
    async sendInvoice () {
      await this.$http.post('/api/vismasync/invoice', this.INVOICE_BY_ID(this.$props.id).data)
    },
    getDeliveryName (articleNbr) {
      try {
        const vismaArticle = this.$store.getters.VISMA_ARTICLE_BY_ID(articleNbr)
        const art = this._.trimEnd(this._.join(this._.dropRight(this._.split(vismaArticle.data.name, ' '), 2), ' '))
        return art + ' ' + vismaArticle.data.distance + ' km'
      } catch (e) {
        return 'No Article'
      }
    },
    isMinimalDelivery (articlePrice, groupPrice) {
      return groupPrice <= 3207 && articlePrice <= 3207
    },
    stripOrderPreamble (orderId) {
      return this._.trimStart(this._.trimStart(orderId, 'FO-0'), 'SO')
    },
    getFeralcoArticle (item) {
      return this.FERALCOARTICLE_BY_NAME(item).data.descr
    },
    createDeliveryText (orderId, articleNbr, price, item) {
      const orderStr = this.stripOrderPreamble(orderId)
      const deliveryStr = this.getDeliveryName(articleNbr)
      return deliveryStr + ' (' + Math.round(price).toFixed(0) + ' +DMT) ' + orderStr + ' ' + this.getFeralcoArticle(item)
    },
    createVismaSeparatorElement (groupData) {
      return {
        tag: 'Separator',
        subContractor: groupData.subContractor,
        article: '',
        text: '',
        price: '',
        amount: '',
        unit: ''
      }
    },
    createVismaMinimalDeliveryElement (groupData, element, nbrDeliveries) {
      const rowString = 'MINIDEB ' + this.createDeliveryText(element.SalesId, element.deliveryArticle, 3207, element.itemId)
      const dmtPrice = this.roundUp(3207 * this.getDmtValue(), 2)
      return this.createVismaDelivery(
        groupData,
        element.deliveryArticle,
        rowString,
        parseFloat(dmtPrice.toFixed(2)),
        parseFloat((1 / nbrDeliveries).toFixed(2)),
        'St'
      )
    },
    createVismaSpecialDeliveryElement (groupData, element, info) {
      const rowString = info.name + ' (' + Math.round(info.price).toFixed(0) + ' +DMT) ' + this.stripOrderPreamble(element.SalesId) + ' ' + this.getFeralcoArticle(element.itemId)
      const dmtPrice = this.roundUp(info.price * this.getDmtValue(), 2)
      return this.createVismaDelivery(
        groupData,
        element.deliveryArticle,
        rowString,
        parseFloat(dmtPrice.toFixed(2)),
        parseFloat(element.loadedAmount.toFixed(2))
      )
    },
    createVismaDeliveryElement (groupData, element) {
      const rowString = this.createDeliveryText(element.SalesId, element.deliveryArticle, groupData.price, element.itemId)
      const dmtPrice = this.roundUp(Math.round(groupData.price) * this.getDmtValue(), 2)
      return this.createVismaDelivery(
        groupData,
        element.deliveryArticle,
        rowString,
        parseFloat(dmtPrice.toFixed(2)),
        parseFloat(element.loadedAmount.toFixed(2))
      )
    },
    createVismaDelivery (groupData, article, text, price, amount, unit = 'Ton') {
      return {
        tag: 'Delivery',
        subContractor: groupData.subContractor,
        article: article,
        text: text,
        price: price,
        amount: amount,
        unit: unit
      }
    },
    createVismaErrorElement (groupData, text) {
      return {
        tag: 'Error',
        subContractor: groupData.subContractor,
        article: 'ERROR',
        text: 'Order error ' + text,
        price: 0,
        amount: 0,
        unit: ''
      }
    },
    createVismaUnannouncedDelivery (groupData, salesId, location, product) {
      return {
        tag: 'Unannounced',
        subContractor: groupData.subContractor,
        article: this.getArticleNumber('990099', product),
        text: 'EXTRA LOSSNING (oaviserad) ' + location + ' ' + this.stripOrderPreamble(salesId),
        price: 754,
        amount: 1,
        unit: 'St'
      }
    },
    createVismaExtraDelivery (groupData, orderId, nbr, product) {
      return {
        tag: 'Extra',
        subContractor: groupData.subContractor,
        article: this.getArticleNumber('990090', product),
        text: 'EXTRA LOSSNINGSKOSTNAD ' + this.stripOrderPreamble(orderId),
        price: 754,
        amount: nbr,
        unit: 'St'
      }
    },
    createVismaWash (groupData, nbr, loc, product) {
      return {
        tag: 'Wash',
        subContractor: groupData.subContractor,
        article: this.getArticleNumber('990190', product),
        text: 'TVÄTTNING (' + loc + ')',
        price: 754,
        amount: nbr,
        unit: 'St'
      }
    },
    createBlowOver (groupData, nbr, product) {
      return {
        tag: 'Extra',
        subContractor: groupData.subContractor,
        article: this.getArticleNumber('990290', product),
        text: 'ÖVERBLÅSNING från släp till bil',
        price: 754,
        amount: nbr,
        unit: 'St'
      }
    },
    createVismaWait (groupData, orderId, nbr, product) {
      return {
        tag: 'Wait',
        subContractor: groupData.subContractor,
        article: this.getArticleNumber('990390', product),
        text: 'VÄNTETID (ej orsakad av oss) ' + this.stripOrderPreamble(orderId),
        price: 860,
        amount: nbr,
        unit: 'Tim'
      }
    },
    createVismaWorkItem (groupData) {
      return {
        tag: 'Work',
        subContractor: groupData.subContractor,
        article: groupData.workItem.workArticle,
        text: groupData.workItem.workText,
        price: groupData.workItem.workCost,
        amount: 1,
        unit: 'St'
      }
    },
    crateVismaForced (groupData, orderId, article, product) {
      let text = ''
      let price = 0
      switch (article) {
        case '990397':
          text = 'FORCERINGSAVGIFT, best före kl 10.00, lev dag ett'
          price = 1035
          break
        case '990398':
          text = 'FORCERINGSAVGIFT, best eft kl 10.00, lev dag ett'
          price = 1551
          break
        case '990399':
          text = 'FORCERINGSAVGIFT, leverans samma dag'
          price = 2068
          break
        default:
          return this.createVismaErrorElement(groupData, article)
      }
      return {
        tag: 'Forced',
        subContractor: groupData.subContractor,
        article: this.getArticleNumber(article, product),
        text: text + ' ' + this.stripOrderPreamble(orderId),
        price: price,
        amount: 1,
        unit: 'St'
      }
    },
    roundDuration (duration) {
      const reminder = duration.asMinutes() % 30
      if (reminder < 10) {
        duration.subtract(reminder, 'minutes')
      } else {
        duration.add(30 - reminder, 'minutes')
      }
      return duration.asHours()
    },
    createVismaInvoice (groupedData) {
      let vismaData = []
      this.setDeliveryPeriod(groupedData)
      for (const group of groupedData) {
        const gd = group[1]
        if (!gd.correct) {
          vismaData.push(this.createVismaErrorElement(gd))
          continue
        }

        if (gd.totalAmount * gd.price > 3207) {
          gd.deliveries.forEach(delivery => vismaData.push(gd.createDeliveryElement(gd, delivery)))
        } else {
          gd.deliveries.forEach(delivery => vismaData.push(this.createVismaMinimalDeliveryElement(gd, delivery, gd.deliveries.length)))
        }
        gd.unannouncedDeliveryLocations.forEach(d => vismaData.push(this.createVismaUnannouncedDelivery(gd, d.salesId, d.name, gd.itemId)))

        const extraDeliveries = gd.nbrDeliveries - gd.includedDeliveries
        if (extraDeliveries > 0) {
          let includedDeliveries = gd.includedDeliveries
          gd.deliveries.forEach(delivery => {
            if (delivery.deliveryNbr > includedDeliveries) {
              vismaData.push(this.createVismaExtraDelivery(gd, delivery.SalesId, delivery.deliveryNbr - includedDeliveries, gd.itemId))
            }
            includedDeliveries -= includedDeliveries > 0 ? gd.includedDeliveries : 0
          })
        }

        if (gd.countWashes && gd.washes.length > 0) {
          let vismaWash = []
          gd.washes.forEach((el) => {
            const wash = this.createVismaWash(gd, el.amount, el.washPlace, gd.itemId)
            const wIdx = vismaWash.findIndex((w) => w.text === wash.text)
            if (wIdx !== -1) {
              vismaWash[wIdx].amount += wash.amount
            } else {
              vismaWash.push(wash)
            }
          })
          vismaData = vismaData.concat(vismaWash)
        }

        const remainingBlowovers = extraDeliveries > 0 ? gd.nbrBlowOvers : extraDeliveries + gd.nbrBlowOvers
        if (remainingBlowovers > 0) {
          vismaData.push(this.createBlowOver(gd, remainingBlowovers, gd.itemId))
        }
        if (moment.duration(gd.totalWaitTime).asHours()) {
          gd.deliveries.forEach(delivery => {
            if (moment.duration(delivery.waitTime).asHours()) {
              vismaData.push(this.createVismaWait(gd, delivery.SalesId, this.roundDuration(moment.duration(delivery.waitTime)), gd.itemId))
            }
          })
        }
        if (gd.forcedArticle) {
          gd.deliveries.forEach(delivery => {
            if (delivery.forcedArticle) {
              vismaData.push(this.crateVismaForced(gd, delivery.SalesId, gd.forcedArticle, gd.itemId))
            }
          })
        }
        if (gd.workItem) {
          vismaData.push(this.createVismaWorkItem(gd))
        }
        vismaData.push(this.createVismaSeparatorElement(gd))
      }
      vismaData.pop()
      return vismaData
    },
    infoTable () {
      return [
        {
          range: { low: 0, high: 18.499 },
          nbr: 1,
          col: 'first'
        },
        {
          range: { low: 24.99, high: 30.09 },
          nbr: 1,
          col: 'second'
        },
        {
          range: { low: 30.09, high: 35.09 },
          nbr: 1,
          col: 'third'
        },
        {
          range: { low: 35.09, high: 40.09 },
          nbr: 1,
          col: 'fourth'
        },
        {
          range: { low: 40.09, high: 42.09 },
          nbr: 1,
          col: 'fifth'
        },
        {
          range: { low: 42.09, high: 50 },
          nbr: 1,
          col: 'sixth'
        }
      ]
    },
    specialTable () {
      return [
        {
          range: { low: 42.09, high: 50 },
          nbr: 2,
          match: ['35', '10176'],
          price: 516,
          article: '986595',
          name: 'GRYCKSBO Special'
        },
        {
          range: { low: 42.09, high: 50 },
          nbr: 1,
          match: ['199_S', '10088'],
          price: 555,
          article: '954795',
          name: 'SKUTSKÄR Special'
        },
        {
          range: { low: 42.09, high: 50 },
          nbr: 1,
          match: ['149', '10055'],
          price: 509,
          article: '918995',
          name: 'HALLSTAVIK Special'
        }
      ]
    },
    replaceTable () {
      return [
        {
          range: { low: 0, high: 50 },
          nbr: 10,
          replaceArticle: '887290',
          price: 704,
          distance: 623,
          article: '887295',
          name: 'MARIEHAMN (ÅLAND)',
          workCost: 10434,
          workArticle: '890951',
          workText: 'Arbetskostnad på Åland',
          countWashes: false
        },
        {
          range: { low: 0, high: 50 },
          nbr: 10,
          replaceArticle: '987290',
          price: 476,
          distance: 439,
          article: '987295',
          name: 'MARIEHAMN (ÅLAND)',
          workCost: 10434,
          workArticle: '990951',
          workText: 'Arbetskostnad på Åland',
          countWashes: false
        },
        {
          range: { low: 0, high: 50 },
          nbr: 10,
          replaceArticle: '871990',
          price: 282,
          distance: 386,
          article: '871995',
          name: 'VISBY (GOTLAND)',
          workCost: 11925,
          workArticle: '890921',
          workText: 'Arbetskostnad på Gotland',
          countWashes: false
        },
        {
          range: { low: 0, high: 50 },
          nbr: 10,
          replaceArticle: '839890',
          price: 282,
          distance: 423,
          article: '839895',
          name: 'LÄRBRO (GOTLAND)',
          workCost: 11925,
          workArticle: '890921',
          workText: 'Arbetskostnad på Gotland',
          countWashes: false
        },
        {
          range: { low: 0, high: 50 },
          nbr: 10,
          replaceArticle: '820690',
          price: 282,
          distance: 435,
          article: '820695',
          name: 'HEMSE (GOTLAND)',
          workCost: 11925,
          workArticle: '890921',
          workText: 'Arbetskostnad på Gotland',
          countWashes: false
        },
        {
          range: { low: 0, high: 50 },
          nbr: 1,
          replaceArticle: '872090',
          price: 344,
          distance: 286,
          article: '872095',
          name: 'VISINGSÖ',
          workCost: 5280,
          workArticle: '890991',
          workText: 'Arbetskostnad på Visingsö',
          countWashes: true
        },
        {
          range: { low: 0, high: 50 },
          nbr: 1,
          replaceArticle: '972090',
          price: 150,
          distance: 120,
          article: '872095',
          name: 'VISINGSÖ',
          workCost: 5280,
          workArticle: '890991',
          workText: 'Arbetskostnad på Visingsö',
          countWashes: true
        }
      ]
    },
    getPriceCol (amount) {
      return this.infoTable().find(el => (this._.inRange(amount, el.range.low, el.range.high))).col
    },
    getPrice (article, amount) {
      if (article === '') return 0
      const vArticle = this.VISMA_ARTICLE_BY_ID(article)
      const col = this.getPriceCol(amount)
      return parseFloat(vArticle.data.price[col])
    },
    decideArticle (current, articles) {
      return articles.reduce((prev, curr) => this.getFurtherstDistanceArticle(prev, curr), current)
    },
    getDeliveryArticles (actions, product) {
      return actions.reduce((prev, curr) => {
        if (curr.name === 'Lossar') {
          if (curr.deliveryPlaceArtNr !== '') {
            return [...prev, this.getArticleNumber(curr.deliveryPlaceArtNr, product)]
          }
        }
        return prev
      }, [])
    },
    countWashes (actions) {
      return this._(actions.filter(a => a.name === 'Tvätt'))
        .groupBy('washPlace')
        .map((a, b) => ({washPlace: b, amount: a.reduce((prev, cur) => prev + parseInt(cur.amount), 0)}))
        .value()
        .filter(a => a.amount !== 0)
    },
    countBlowovers (actions) {
      const blowovers = actions.filter(a => ((a.name === 'Läktring') || (a.name === 'Överblåsning')))
      return blowovers.reduce((prev, curr) => {
        return prev + Number(curr.amount)
      }, 0)
    },
    countWaitTime (actions) {
      const waitTimes = actions.filter(a => a.name === 'Väntetid')
      return waitTimes.reduce((prev, curr) => {
        return prev.add(moment(curr.endTime).diff(moment(curr.startTime)))
      }, moment.duration(0))
    },
    countDeliveredAmount (actions, groupedLoading) {
      const loadedAmounts = actions.filter(a => a.name === 'Lossar' || (groupedLoading && a.name === 'Retur'))
      return loadedAmounts.reduce((prev, curr) => {
        const val = curr.amount === '' ? 0 : parseFloat(curr.amount)
        return prev + val
      }, 0)
    },
    countReturnedAmount (actions) {
      return actions.filter(a => a.name === 'Retur').reduce((prev, curr) => {
        return prev + parseFloat(curr.amount)
      }, 0)
    },
    countDeliveries (actions) {
      return actions.filter(a => a.name === 'Lossar' && a.unAnnounced !== true).length
    },
    getUnannouncedDeliveries (salesId, actions) {
      return actions.filter(a => a.name === 'Lossar' && a.unAnnounced === true).map(u => {
        return {
          salesId: salesId,
          name: u.deliveryPlaceName
        }
      })
    },
    getReturns (salesId, actions) {
      return actions.filter(a => a.name === 'Retur').map(u => {
        return {
          salesId: salesId,
          name: u.name
        }
      })
    },
    getGroup (order) {
      return !order.data.GroupLoading || order.data.GroupLoading.length === 0 ? 'NoGroup' + order.id : order.data.GroupLoading
    },
    feralcoIntern2feralco (order) {
      if (order.type !== 'feralcoIntern') {
        return order
      }
      let feralcoOrder = order

      // Decide Article
      for (let i = 0; i < feralcoOrder.data.orderRows[0].actions.length; i++) {
        if (feralcoOrder.data.orderRows[0].actions[i].name === 'Lossar') {
          feralcoOrder.data.orderRows[0].actions[i].deliveryPlaceArtNr = '900095'
        }
        feralcoOrder.data['ItemId'] = feralcoOrder.data.productId
      }

      // Create Delivery
      feralcoOrder.data['SalesId'] = feralcoOrder.data.fraktsedelNr

      feralcoOrder.data['forced'] = false
      feralcoOrder.data['DeliveryPlace'] = '1089'
      feralcoOrder.data['Quantity'] = feralcoOrder.data.quantity

      return feralcoOrder
    },
    groupInvoices (orders) {
      let subContractors = []
      let invoiceGrouped = {}
      for (const order of orders) {
        const group = this.getGroup(order)
        !(group in invoiceGrouped) && (invoiceGrouped[group] = {
          createDeliveryElement: this.createVismaDeliveryElement,
          deliveryPlace: [],
          deliveries: [],
          includedDeliveries: 0,
          deliveryDate: {},
          totalWaitTime: moment.duration(0),
          nbrBlowOvers: 0,
          washes: [],
          nbrDeliveries: 0,
          unannouncedDeliveryLocations: [],
          totalAmount: 0,
          totalOrderedAmount: 0,
          returnedAmount: 0,
          priceArticle: '',
          price: 0,
          itemId: '',
          correct: true,
          rest: 0,
          forcedArticle: '',
          subContractor: '',
          workItem: undefined,
          countWashes: true,
          containsIntern: false
        })
        const o = this.feralcoIntern2feralco(order)
        const nrbDeliveries = this.countDeliveries(o.data.orderRows[0].actions)
        const waitTime = this.countWaitTime(o.data.orderRows[0].actions)
        const loadedAmount = this.countDeliveredAmount(o.data.orderRows[0].actions, group.startsWith('NoGroup'))
        const deliveryArticle = this.decideArticle('', this.getDeliveryArticles(o.data.orderRows[0].actions, o.data.ItemId))
        const newDelivery = this.createDelivery(deliveryArticle, loadedAmount, nrbDeliveries, waitTime, o.data)
        const washes = this.countWashes(o.data.orderRows[0].actions)
        this.setForced(invoiceGrouped[group], o.data.forced)
        invoiceGrouped[group].deliveryPlace.push(o.data.DeliveryPlace)
        invoiceGrouped[group].itemId = o.data.ItemId
        invoiceGrouped[group].deliveries.push(newDelivery)
        invoiceGrouped[group].deliveries.sort((a, b) => b.distance - a.distance)
        invoiceGrouped[group].totalWaitTime.add(waitTime)
        invoiceGrouped[group].nbrBlowOvers += this.countBlowovers(o.data.orderRows[0].actions)
        invoiceGrouped[group].washes = [...invoiceGrouped[group].washes, ...washes]
        invoiceGrouped[group].nbrDeliveries += nrbDeliveries
        invoiceGrouped[group].unannouncedDeliveryLocations = [
          ...invoiceGrouped[group].unannouncedDeliveryLocations,
          ...this.getUnannouncedDeliveries(o.data.SalesId, o.data.orderRows[0].actions),
          ...this.getReturns(o.data.SalesId, o.data.orderRows[0].actions)
        ]
        invoiceGrouped[group].totalAmount += loadedAmount
        invoiceGrouped[group].totalAmount -= this.countReturnedAmount(o.data.orderRows[0].actions)
        invoiceGrouped[group].returnedAmount += this.countReturnedAmount(o.data.orderRows[0].actions)
        invoiceGrouped[group].totalOrderedAmount += parseFloat(o.data.Quantity.replace(',', '.'))
        invoiceGrouped[group].priceArticle = this.decideArticle(invoiceGrouped[group].priceArticle, [newDelivery.deliveryArticle])
        const groupSubcontractor = this.TRUCKS_BY_ID(o.data.truck).data.subcontractor
        if (groupSubcontractor !== undefined) {
          invoiceGrouped[group].subContractor = groupSubcontractor
          subContractors = this._.union(subContractors, [groupSubcontractor])
        }
      }
      for (const priceGroup in invoiceGrouped) {
        invoiceGrouped[priceGroup].rest = invoiceGrouped[priceGroup].totalAmount - invoiceGrouped[priceGroup].returnedAmount
        invoiceGrouped[priceGroup].deliveryDate = invoiceGrouped[priceGroup].deliveries[0].deliveryDate
        this.adjustToMinimum(invoiceGrouped[priceGroup])
        this.replaceSpecial(invoiceGrouped[priceGroup])
        this.replaceReplace(invoiceGrouped[priceGroup])
        this.setPrice(invoiceGrouped[priceGroup])
        this.setIncludedDeliveries(invoiceGrouped[priceGroup])
      }
      this.subContractors.push(...subContractors)
      return Object.entries(invoiceGrouped).sort((a, b) => a[1].deliveryDate.valueOf() - b[1].deliveryDate.valueOf())
    },
    createDelivery (deliveryArticle, loadedAmount, deliveryNbr, waitTime, orderData) {
      return {
        SalesId: orderData.SalesId,
        itemId: orderData.ItemId,
        deliveryArticle: deliveryArticle,
        deliveryDate: this.getDeliveryDate(orderData.orderRows),
        deliveryNbr: deliveryNbr,
        waitTime: waitTime,
        distance: this.$store.getters.VISMA_ARTICLE_BY_ID(deliveryArticle).data.distance,
        loadedAmount: loadedAmount,
        forcedArticle: orderData.forced
      }
    },
    getInfoTableInRangeForAmount (amount) {
      return this.infoTable().find(el => (this._.inRange(amount, el.range.low, el.range.high)))
    },
    getIncludedDeliveries (group) {
      let infoEntry = this.getInfoTableInRangeForAmount(group.totalOrderedAmount)
      if (infoEntry === undefined && group.totalOrderedAmount > group.totalAmount) {
        infoEntry = this.getInfoTableInRangeForAmount(group.totalAmount)
      }
      return infoEntry
    },
    setIncludedDeliveries (group) {
      try {
        group.includedDeliveries = group.includedDeliveries !== 0 ? group.includedDeliveries : this.getIncludedDeliveries(group).nbr
      } catch (e) {
        group.includedDeliveries = 0
        group.correct = false
      }
    },
    setPrice (group) {
      try {
        group.price = group.price !== 0 ? group.price : this.getPrice(group.priceArticle, group.totalOrderedAmount)
      } catch (e) {
        try {
          group.price = group.price !== 0 ? group.price : this.getPrice(group.priceArticle, group.totalAmount)
        } catch (ee) {
          group.price = 0
          group.correct = false
        }
      }
    },
    setForced (group, article) {
      if (group.forcedArticle === article || !article) {
        return
      }
      if (group.forcedArticle !== '' && article) {
        group.forcedArticle = 'Olika forceringsartiklar, korrigera'
        return
      }
      group.forcedArticle = article
    },
    adjustToMinimum (group) {
      if (!this._.inRange(group.totalOrderedAmount, this.infoTable()[0].range.high, this.infoTable()[1].range.low)) {
        return
      }
      const diff = this._.round(25 - group.totalOrderedAmount, 2)
      const split = this._.round(diff / group.deliveries.length, 2)
      let largestIdx = -1
      let largestAmount = 0
      let addedAmount = 0
      group.deliveries.forEach((e, i) => {
        if (largestAmount < group.deliveries[i].loadedAmount) {
          largestAmount = group.deliveries[i].loadedAmount
          largestIdx = i
        }
        group.deliveries[i].loadedAmount += split
        addedAmount += split
      })
      group.deliveries[largestIdx].loadedAmount += diff - addedAmount
      group.totalAmount = 25.00
      group.totalOrderedAmount = 25.00
    },
    isSpecial (specialDescr, deliveryPlaces, totalOrderedAmount) {
      return (deliveryPlaces.filter(v => specialDescr.match.includes(v)).length === deliveryPlaces.length) &&
        this._.inRange(totalOrderedAmount, specialDescr.range.high, specialDescr.range.low)
    },
    replaceSpecial (group) {
      this.specialTable().forEach(value => {
        if (this.isSpecial(value, group.deliveryPlace, group.totalOrderedAmount)) {
          group.createDeliveryElement = (groupData, element) => {
            const info = value
            return this.createVismaSpecialDeliveryElement(groupData, element, info)
          }
          group.includedDeliveries = value.nbr
          group.price = value.price
          group.priceArticle = this.getArticleNumber(value.article, group.itemId)
          group.deliveries.forEach((delivery) => (delivery.deliveryArticle = this.getArticleNumber(value.article, delivery.itemId)))
        }
      })
    },
    replaceInfoValue (element, value) {
      return element.deliveryArticle === value.replaceArticle ? value : {
        name: this.getDeliveryName(element.deliveryArticle),
        price: value.price
      }
    },
    replaceReplace (group) {
      this.replaceTable().forEach(value => {
        if (value.replaceArticle === group.priceArticle) {
          group.createDeliveryElement = (groupData, element) => {
            const info = this.replaceInfoValue(element, value)
            return this.createVismaSpecialDeliveryElement(groupData, element, info)
          }
          group.countWashes = value.countWashes
          group.includedDeliveries = value.nbr
          group.price = value.price
          group.priceArticle = value.article
          group.workItem = {
            workArticle: value.workArticle,
            workCost: value.workCost,
            workText: value.workText
          }
        }
      })
    },
    createInvoiceRows (id) {
      let invoiceGrouped = this.groupInvoices(this.filterOrders(id).map(flat => flat.Order))
      return invoiceGrouped
    },
    filterOrders (id) {
      const orders = this.$store.getters.INVOICE_BY_ID(id).invoiceDatas
      return orders
    },
    getArticleNumber (articleNbr, product) {
      let realArticleNbr = parseInt(articleNbr)
      if (product.startsWith('SOL')) {
        realArticleNbr -= 100000
      }
      return realArticleNbr.toString()
    },
    replaceArticleMock (article, vArticle) {
      const rep = this.replaceTable().find(value => value.replaceArticle === article)
      if (rep) {
        return {
          data: {
            distance: rep.distance
          },
          artNbr: article
        }
      }
      return vArticle
    },
    getFurtherstDistanceArticle (a, b) {
      if (a === '') {
        return b
      }
      const artA = this.replaceArticleMock(a, this.$store.getters.VISMA_ARTICLE_BY_ID(a))
      const artB = this.replaceArticleMock(b, this.$store.getters.VISMA_ARTICLE_BY_ID(b))
      return artA.data.distance > artB.data.distance ? artA.artNbr : artB.artNbr
    },
    sumAmounts (amounts) {
      return this._.uniq(amounts).reduce((acc, curr) => parseFloat(acc) + parseFloat(curr === '' ? '0.00' : curr), '0.00')
    },
    setDeliveryPeriod (rows) {
      const start = rows[0][1].deliveryDate
      const end = rows[rows.length - 1][1].deliveryDate
      this.deliveryPeriod = start.format('YYYY-MM-DD') + '--' + end.format('YYYY-MM-DD')
    },
    getDeliveryDate (orderRows) {
      const startDate = moment('2019-01-01')
      const date = orderRows.reduce((acc, curr) => {
        return acc.isSameOrBefore(moment(curr.endTime)) ? moment(curr.endTime) : acc
      }, startDate)
      return date
    }
  }
}

</script>



// WEBPACK FOOTER //
// src/components/Invoices/InvoiceFeralcoCreate.vue